/* eslint-disable react-hooks-js/refs, react-hooks/refs -- ref-as-prop forwarding (React 19): every Card primitive destructures `ref` from props and hands it to Base UI's useRender; not a render-phase useRef().current read */
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@/shared/lib/utils";
import {
	getSurfaceClasses,
	resolveSurfaceLevel,
	SURFACE_IDENTITY_SHADOWS,
	SurfaceProvider,
	useSurface,
} from "@/shared/ui/surface";
import type {
	CardContentProps,
	CardDescriptionProps,
	CardFooterProps,
	CardHeaderProps,
	CardProps,
	CardTitleProps,
} from "../types";

/**
 * Base UI's `useRender` types its `props` parameter as `Record<string, unknown>`,
 * but React component props have typed keys without an index signature and are
 * therefore not structurally assignable. This helper performs a localized
 * widening to the shape the hook expects. The cast is safe because every React
 * prop value is assignable to `unknown`; we centralize it in one place rather
 * than sprinkling `as Record<string, unknown>` across every call site. When
 * Base UI ships a generic `useRender` signature this helper can be removed.
 */
function toRenderProps<T extends object>(obj: T): Record<string, unknown> {
	return obj as Record<string, unknown>;
}

/**
 * Cards offset +1 from their substrate so a section card on the page lands
 * at surface level 2, a nested card-in-card lands at 3, a card inside a
 * popover (which already offsets +2) lands at 4, etc. The Card re-provides
 * its resolved level via SurfaceProvider so descendants stack relative to
 * THIS card — not the page.
 *
 * `text-card-foreground` is intentionally retained over the new
 * `text-surface-foreground` to preserve theme-specific overrides shipped
 * with the per-theme CSS files (some themes diverge card foreground from
 * default foreground).
 */
const Card = ({ className, ref, render, children, ...props }: CardProps) => {
	const substrate = useSurface();
	const level = resolveSurfaceLevel(substrate, 1);
	return useRender({
		defaultTagName: "div",
		render,
		props: toRenderProps({
			"data-slot": "card",
			"data-surface-level": String(level),
			className: cn(
				"rounded-xl border text-card-foreground",
				// Background floats with the substrate; shadow stays the card's
				// raised identity ("a card reads as a card" at any depth).
				getSurfaceClasses(level, {
					shadowLevel: SURFACE_IDENTITY_SHADOWS.raised,
				}),
				className,
			),
			ref,
			...props,
			children: <SurfaceProvider value={level}>{children}</SurfaceProvider>,
		}),
	});
};
Card.displayName = "Card";

const CardHeader = ({ className, ref, render, ...props }: CardHeaderProps) => {
	return useRender({
		defaultTagName: "div",
		render,
		props: toRenderProps({
			"data-slot": "card-header",
			className: cn("flex flex-col space-y-1.5 p-6", className),
			ref,
			...props,
		}),
	});
};
CardHeader.displayName = "CardHeader";

const CardTitle = ({ className, ref, render, ...props }: CardTitleProps) => {
	return useRender({
		defaultTagName: "div",
		render,
		props: toRenderProps({
			"data-slot": "card-title",
			className: cn("font-semibold leading-none tracking-tight", className),
			ref,
			...props,
		}),
	});
};
CardTitle.displayName = "CardTitle";

const CardDescription = ({
	className,
	ref,
	render,
	...props
}: CardDescriptionProps) => {
	return useRender({
		defaultTagName: "div",
		render,
		props: toRenderProps({
			"data-slot": "card-description",
			className: cn("text-body-sm text-muted-foreground", className),
			ref,
			...props,
		}),
	});
};
CardDescription.displayName = "CardDescription";

const CardContent = ({
	className,
	ref,
	render,
	...props
}: CardContentProps) => {
	return useRender({
		defaultTagName: "div",
		render,
		props: toRenderProps({
			"data-slot": "card-content",
			className: cn("p-6 pt-0", className),
			ref,
			...props,
		}),
	});
};
CardContent.displayName = "CardContent";

const CardFooter = ({ className, ref, render, ...props }: CardFooterProps) => {
	return useRender({
		defaultTagName: "div",
		render,
		props: toRenderProps({
			"data-slot": "card-footer",
			className: cn("flex items-center p-6 pt-0", className),
			ref,
			...props,
		}),
	});
};
CardFooter.displayName = "CardFooter";

export {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
};
